"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Popover, PopoverTrigger, PopoverContent, Input, Button, Spinner, ScrollShadow } from "@heroui/react";
import { FilterOption } from "./types";
import Typography from "../typography/Typography";
import { CloseIc, SearchInputIc } from "../icons";

interface ModelSearchSelectProps {
  placeholder: string;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  onSearchChange: (searchTerm: string) => void;
  options: FilterOption[];
  isLoading?: boolean;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'campaign';
  autoWidth?: boolean;
}

const ModelSearchSelect: React.FC<ModelSearchSelectProps> = ({
  placeholder,
  selectedKey,
  onSelectionChange,
  onSearchChange,
  options,
  isLoading = false,
  startContent,
  className = "",
  disabled = false,
  variant = "default",
  autoWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedOption = options.find(option => option.key === selectedKey);

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: "bg-[#f4f4f5] border-none rounded-[8px] !px-[12px] !min-h-[36px] !h-[36px]",
          value: "text-[14px] !text-[#71717a]"
        };
      default:
        return {
          trigger: "h-9 bg-gray-100 border-none rounded-[8px] !px-[12px] hover:bg-gray-200 focus:border-blue-500",
          value: "text-[14px] !text-[#71717a]"
        };
    }
  };

  const variantStyles = getVariantStyles();

  const handleOptionSelect = (option: FilterOption) => {
    onSelectionChange(option.key);
    setIsOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm("");
      onSearchChange("");
    }
  };

  return (
    <div className={autoWidth ? 'w-fit max-w-[320px]' : 'w-full'}>
      <Popover 
        isOpen={isOpen} 
        onOpenChange={handleOpenChange}
        placement="bottom-start"
        classNames={{
          content: "max-w-[340px] w-[340px] py-[8px] px-[16px]"
        }}
      >
        <PopoverTrigger>
          <Button
            className={`${variantStyles.trigger} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${autoWidth ? 'w-fit max-w-[320px]' : 'w-full'} ${className} justify-start`}
            disabled={disabled}
          >
            <div className="flex items-center gap-[4px] w-full">
              {selectedOption && selectedOption.key !== 'all' && selectedOption.avatar ? (
                <img src={selectedOption.avatar} className="w-[20px] h-[20px] rounded-[4px]" alt={selectedOption.name || ''} />
              ) : (
                startContent
              )}
              <span className={`${selectedOption && selectedOption.key !== 'all' ? 'text-[14px] !text-[#11181C]' : variantStyles.value} truncate flex-1 text-left`}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="w-full flex flex-col gap-[8px]">
            <div className="flex flex-col gap-[8px]">
              <Typography variant="body2">
                Аккаунт
              </Typography>
              <Input
                type="text"
                placeholder="Поиск по имени модели"
                size="sm"
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(event.target.value)}
                endContent={<SearchInputIc/>}
                classNames={{
                  input: "text-sm",
                  inputWrapper: "!min-h-[36px] !max-h-[36px]",
                }}
              />
            </div>
            <ScrollShadow className="max-h-[200px] overflow-y-auto" aria-label="Навигационное меню">
              
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Spinner size="sm" />
                </div>
              ) : options.length === 0 ? (
                <div className="text-sm text-gray-500">
                  {searchTerm ? 'Модели не найдены' : 'Начните вводить для поиска'}
                </div>
              ) : (
                <>
                  {options.filter(option => option.key !== 'all').map((option:any) => {
                    return(
                    <div
                      key={option.key}
                      className={`flex items-center justify-between gap-[8px] w-full text-left p-[4px] rounded-[8px] hover:bg-gray-100 cursor-pointer text-sm whitespace-normal break-words ${
                        selectedKey === option.key ? 'bg-[#EEEEEF] ' : ''
                      }`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      <div className="flex items-center gap-[8px]">
                        <img src={option?.avatar} className="w-[24px] h-[24px] rounded-[5px]"/>
                        <span>{option.name}</span>
                        <a target="__blank" href={`https://onlyfans.com/${option.username}`} className="text-blue-600 hover:underline flex items-center gap-1">
                          <Typography variant="body2" className="!text-[#006FEE]">
                            @{option.username}
                          </Typography>
                        </a>
                      </div>
                      {selectedKey === option.key &&
                        <Button
                          //@ts-ignore
                          size="xs"
                          className="h-[20px] rounded-[8px] !w-[20px] p-0 bg-transparent"
                          onPress={() => handleOptionSelect({ key: 'all', label: 'Модель' })}
                        >
                          <CloseIc/>
                        </Button>
                      }
                    </div>
                  )})}
                  {searchTerm && options.filter(option => option.key !== 'all').length === 0 && (
                    <div className="text-sm text-gray-500 p-[4px]">
                      Модели не найдены
                    </div>
                  )}
                </>
              )}
            </ScrollShadow>

          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ModelSearchSelect;
